import { DELIVERY_OPTIONS } from "../constants/DeliveryConstants";
import { ORDER_STATUS_SHIPPED } from "../constants/OrderConstant";
import { compareDates } from "./workDay";

/**
 * Which carrier already has an order: the double-booking guard of every export on the orders
 * page, and since HVC-82 also what decides which shipping label an order needs
 * (functions/fulfilment.js). Moved here from views/app-views/orders/index.js unchanged.
 *
 * Pure: no Firestore, no React.
 */

export const CARRIER_TRUNKRS = "trunkrs";
export const CARRIER_SOOCOOL = "soocool";

/**
 * OptimoRoute now writes `optimoRouteExportedAt` on the order (see `functions/api/optimoroute.js`),
 * so it is a real booking like Trunkrs and SooCool. It used to write only `orderStatus = SHIPPED`,
 * which meant it could not be told apart from any other carrier and could not block one.
 *
 * Orders exported BEFORE that change carry no marker, only SHIPPED, and never get the field
 * retroactively. `isLegacyOptimoRouteBooking` recognises them so they block a second booking all
 * the same; `exportStats` keeps flagging them, because there the attribution is inference rather
 * than something the order itself records.
 */
export const CARRIER_OPTIMOROUTE = "optimoroute";

export const CARRIER_LABELS = {
  [CARRIER_TRUNKRS]: "Trunkrs",
  [CARRIER_SOOCOOL]: "SooCool",
  [CARRIER_OPTIMOROUTE]: "OptimoRoute",
};

/**
 * The first day a regional order could be delivered by SooCool (13 October 2026). OptimoRoute exports
 * of deliveries from that day on were made by a CMS that writes `optimoRouteExportedAt`.
 */
export const OPTIMOROUTE_MARKER_SINCE = "13-10-2026";

/** The order carriers are listed in inside a stats row. */
export const CARRIER_ORDER = [CARRIER_TRUNKRS, CARRIER_SOOCOOL, CARRIER_OPTIMOROUTE];

/**
 * A VCE regional order that is SHIPPED but carries no carrier marker at all: exported to
 * OptimoRoute before `optimoRouteExportedAt` existed (see functions/api/optimoroute.js).
 *
 * Historical orders never get that field retroactively, so without this they read as "still free"
 * and can be handed to SooCool although one of our own vans already took the parcel - a second
 * pickup with nothing on the order to show for it, which is exactly the invisible double booking
 * the marker was added to prevent.
 *
 * Deliberately narrow. Only VCE regional, where OptimoRoute is the single export that could have
 * set SHIPPED. A national order that is SHIPPED without a marker is the ChillBill case and must
 * stay bookable, because moving national - Saturday included - to SooCool is the point of the
 * migration.
 *
 * And only before {@link OPTIMOROUTE_MARKER_SINCE}. From the first SooCool delivery day on, a
 * regional order that is SHIPPED without any marker is something else: a SooCool shipment cancelled
 * on the SooCool page, which keeps its status and loses its `soocool` block (HVC-83 moves exactly those
 * to our own van). Read as "already OptimoRoute", it would vanish from "Nog te doen" while no van has it.
 */
export const isLegacyOptimoRouteBooking = (order) =>
  compareDates(order?.delivery?.date, OPTIMOROUTE_MARKER_SINCE) < 0 &&
  order?.orderStatus === ORDER_STATUS_SHIPPED &&
  order?.shopSource === "VCE" &&
  order?.landelijk === false &&
  // A pickup order can carry `landelijk: false` too, and nothing ever books a carrier for it.
  order?.delivery?.type === DELIVERY_OPTIONS.SEND &&
  !order?.trunkrsNumber &&
  !order?.soocool?.orderId &&
  !order?.optimoRouteExportedAt;

/**
 * Which carrier an order has ALREADY been booked with, or null when it is still free.
 *
 * This exists because Trunkrs and SooCool are both live during the transition
 * (planning/soocool-koppeling.md, besluit 1) while `getOrdersReadyToShip()` deliberately keeps
 * returning orders that are already SHIPPED. Without this check, running Export -> SooCool and
 * then Export -> Trunkrs on the same day books every national order with BOTH carriers: two
 * pickups, two sets of labels, double billing. Both export paths ask this one helper, so the two
 * conditions can never drift apart again.
 *
 * ChillBill never wrote a marker on the order, so an order it carried cannot be recognised here.
 * Its export is gone from the orders page since HVC-82.
 */
export const getCarrierBooking = (order) => {
  if (order?.trunkrsNumber) return CARRIER_TRUNKRS;
  if (order?.soocool?.orderId) return CARRIER_SOOCOOL;
  if (order?.optimoRouteExportedAt) return CARRIER_OPTIMOROUTE;
  // Last, so a real marker always wins: this one is inferred from the status.
  if (isLegacyOptimoRouteBooking(order)) return CARRIER_OPTIMOROUTE;
  return null;
};

/**
 * Split a set of orders into the ones still free to book and the ones a different carrier already
 * has, grouped per carrier so the caller can name that carrier in the notification.
 *
 * Every export path runs its candidates through this, so "SooCool skips what OptimoRoute has" and
 * "OptimoRoute skips what SooCool has" are one rule instead of two that can drift.
 */
export const splitByExistingBooking = (orders, ownCarrier) => {
  const free = [];
  const blockedByCarrier = {};

  orders.forEach((order) => {
    const booked = getCarrierBooking(order);
    // Already booked with ourselves is not a conflict: the per-carrier export decides for itself
    // whether that is a skip (SooCool, via the api) or a re-send.
    if (!booked || booked === ownCarrier) {
      free.push(order);
      return;
    }
    if (!blockedByCarrier[booked]) blockedByCarrier[booked] = [];
    blockedByCarrier[booked].push(order);
  });

  return { free, blockedByCarrier };
};
